import { useEffect, useState } from 'react';

import WarningModal from '../../../components/warning-modal/warning-modal.component';
import { TNController } from '../../../components/toast-notification/toast-notification.component';
import { subscriptionSdk } from '../../../services/subscription.service';
import { FamilyMember, Subscription } from '../../../types/subscription.type';

import styles from './cancel-subscription.module.scss';

type Props = {
    subscriptionId: number;
    onDone: () => void;
};

const CancelSubscriptionModal = ({ subscriptionId, onDone }: Props) => {
    const [sub, setSub] = useState<Subscription | null>(null);
    const [fetching, setFetching] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [understood, setUnderstood] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const result = await subscriptionSdk.getSubscriptionByAdmin(
                subscriptionId
            );
            if (cancelled) return;
            if ('error' in result) {
                TNController.show(result.error, 2500, 'error');
                onDone();
                return;
            }
            setSub(result);
            setFetching(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [subscriptionId, onDone]);

    const onConfirm = async () => {
        if (confirming) return;
        if (
            (sub?.subscriptionType === 'family' ||
                sub?.subscriptionType === 'corporate') &&
            !understood
        )
            return;
        setConfirming(true);
        const result = await subscriptionSdk.cancelSubscription(subscriptionId);
        setConfirming(false);
        if ('error' in result) {
            return TNController.show(result.error, 2500, 'error');
        }
        TNController.show('Suscripción eliminada exitosamente', 2500);
        onDone();
    };

    if (fetching || !sub) {
        return (
            <WarningModal
                title="Eliminar suscripción"
                body="Cargando datos de la suscripción..."
                onConfirm={onDone}
                confirmText="Cerrar"
            />
        );
    }

    if (sub.subscriptionType === 'family') {
        const activeMembers = (sub.members ?? []).filter(
            (member) => member.status !== 'removed'
        );
        const memberCount = sub.memberCount ?? activeMembers.length;
        return (
            <WarningModal
                title="Eliminar Plan Familia"
                body={`Esta suscripción es un Plan Familia. Al eliminarla, se darán de baja el titular y los ${memberCount} miembros (incluyendo invitaciones pendientes de aceptar).`}
                note="Esta acción es irreversible. Los miembros afectados perderán el acceso al servicio de forma inmediata."
                confirmText={`Sí, eliminar ${memberCount} suscripciones`}
                confirmLoading={confirming}
                confirmDisabled={!understood}
                onConfirm={onConfirm}
                onDiscard={onDone}
                extra={
                    <>
                        <div className={styles.members}>
                            {activeMembers.map((m) => (
                                <FamilyMemberRow key={m.id} member={m} />
                            ))}
                        </div>
                    </>
                }
                checkbox={{
                    label: `Entiendo que voy a eliminar ${memberCount} suscripciones.`,
                    checked: understood,
                    onChange: setUnderstood,
                }}
            />
        );
    }

    if (sub.subscriptionType === 'corporate') {
        return (
            <WarningModal
                title="Eliminar suscripción corporativa"
                body={`Esta suscripción pertenece a la empresa ${
                    sub.company?.name ?? 'corporativa'
                }. Eliminarla afecta la cobertura de todos los miembros asociados al equipo.`}
                note="Esta acción es irreversible. La empresa quedará sin la suscripción activa en el sistema."
                confirmText="Sí, eliminar"
                confirmLoading={confirming}
                confirmDisabled={!understood}
                onConfirm={onConfirm}
                onDiscard={onDone}
                checkbox={{
                    label: 'Entiendo el impacto sobre la suscripción corporativa.',
                    checked: understood,
                    onChange: setUnderstood,
                }}
            />
        );
    }

    return (
        <WarningModal
            title="Eliminar suscripción"
            body="Si confirmas esta acción la suscripción se eliminará inmediatamente y no podrá reactivarse. ¿Estás seguro de querer continuar?"
            confirmText="Sí, eliminar suscripción"
            discardText="No, volver"
            confirmLoading={confirming}
            onConfirm={onConfirm}
            onDiscard={onDone}
        />
    );
};

const FamilyMemberRow = ({ member }: { member: FamilyMember }) => {
    const name =
        member.firstName || member.lastName
            ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()
            : member.email;
    return (
        <div className={styles.memberRow}>
            <div className={styles.memberInfo}>
                <span className={styles.memberName}>{name}</span>
                {(member.firstName || member.lastName) && (
                    <span className={styles.memberEmail}>{member.email}</span>
                )}
            </div>
            <span className={styles.cancelBadge}>se eliminará</span>
        </div>
    );
};

export default CancelSubscriptionModal;
